import {
  Card,
  FlexBox,
  H2,
  SkeletonText,
  SkeletonTextLine,
  StatusMessage,
  ToggleSwitch,
  ToggleSwitchOffLabel,
  ToggleSwitchOnLabel,
  Typography,
} from '@vp/swan';
import {
  useIdentity,
  useSwanStyleKeys,
  useUserContext,
} from '@vp/ubik-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from '@vp/om-translations';
import { initializeNewRelicTrackingContainer } from '@vp/om-newrelic-tracker';
import {
  getShopperFulfillmentPreferences,
  patchShopperFulfillmentPreferences,
} from '../api/shopperPreferencesApi';
import { useEffect } from 'react';
import { useErrorContext } from './contexts/ErrorContext';
import { NR_AGENT_ID } from '../constants/newRelic';
import { aggregateNRCommonData } from '../utils/auth';

export interface Props {
  whiteLabelShippingEnabled?: boolean;
}

const GET_SHOPPER_FULFILLMENT_PREFERENCES = 'getShopperFulfillmentPreferences';

export const WhiteLabelPreference = ({
  whiteLabelShippingEnabled = false,
}: Props) => {
  useSwanStyleKeys(['core', 'toggleSwitch', 'statusMessage']);
  const { auth } = useIdentity();
  const queryClient = useQueryClient();
  const { locale } = useUserContext();

  const profile = auth?.getProfile();
  const { canonicalId, isAnonymousUser, isInternalUser } =
    aggregateNRCommonData(profile);

  const [sectionLabels, fulfillmentPreferencesMessages, errorMessages] =
    useTranslations([
      'sectionLabels',
      'fulfillmentPreferencesMessages',
      'errorMessages',
    ]);
  const { errorMessage, setErrorMessage, clearError } = useErrorContext();

  const { isLoading, data, isError } = useQuery({
    queryKey: [GET_SHOPPER_FULFILLMENT_PREFERENCES],
    queryFn: getShopperFulfillmentPreferences,
    enabled: whiteLabelShippingEnabled && !!auth?.getAuthorizationHeader(),
  });

  useEffect(() => {
    if (isError) {
      setErrorMessage(errorMessages.getShopperFulFillmentPreferences);
    }
  }, [
    isError,
    setErrorMessage,
    errorMessages.getShopperFulFillmentPreferences,
  ]);

  useEffect(() => {
    // don't bother initializing the tracking container until the properties are all available
    if (profile && locale) {
      initializeNewRelicTrackingContainer({
        agentId: NR_AGENT_ID,
        staticApplicationData: {
          shopperId: isInternalUser ? '' : canonicalId,
          locale,
          additionalAttributes: {
            isAnonymousUser: isAnonymousUser,
            isInternalUser: isInternalUser,
            canonicalId: canonicalId,
          },
        },
      });
    }
  }, [canonicalId, isAnonymousUser, isInternalUser, locale, profile]);

  const savePreferenceMutation = useMutation({
    mutationFn: ({ whiteLabel }: { whiteLabel: boolean }) =>
      patchShopperFulfillmentPreferences(whiteLabel),
    onMutate: async (preference) => {
      // get the previous value of the toggle
      const previousValue = queryClient.getQueryData([
        GET_SHOPPER_FULFILLMENT_PREFERENCES,
      ]);

      // optimistically update the toggle value
      queryClient.setQueryData(
        [GET_SHOPPER_FULFILLMENT_PREFERENCES],
        preference.whiteLabel
      );

      return previousValue;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: [GET_SHOPPER_FULFILLMENT_PREFERENCES],
      });
    },
    onError: (_err, _variables, previousValue) => {
      // on failure rollback to the previous value
      queryClient.setQueryData(
        [GET_SHOPPER_FULFILLMENT_PREFERENCES],
        previousValue
      );
      setErrorMessage(errorMessages.changeShopperFulfillmentPreference);
    },
  });

  const setPreference = (activated: boolean) => {
    clearError();
    savePreferenceMutation.mutate({
      whiteLabel: activated,
    });
  };

  if (!whiteLabelShippingEnabled || !auth?.getAuthorizationHeader()) {
    return <></>;
  }

  return (
    <FlexBox gap="3" flexDirection="column">
      <FlexBox gap="to-actions" justifyContent="space-between">
        <H2 fontSize="large" id="white-label-shipping-toggle">
          {sectionLabels.shopperFulfillmentPreferences}
        </H2>
        <ToggleSwitch
          activated={!!data?.preferences?.whiteLabel}
          onRequestActivatedChange={(activated: boolean) =>
            setPreference(activated)
          }
          aria-labelledby="white-label-shipping-toggle"
          m="0"
        >
          <ToggleSwitchOnLabel>
            {fulfillmentPreferencesMessages.toggleOn}
          </ToggleSwitchOnLabel>
          <ToggleSwitchOffLabel>
            {fulfillmentPreferencesMessages.toggleOff}
          </ToggleSwitchOffLabel>
        </ToggleSwitch>
      </FlexBox>
      <FlexBox gap="to-actions" flexDirection="column">
        {errorMessage && (
          <StatusMessage skin="error">{errorMessage}</StatusMessage>
        )}
        <Card mb="6" bordered evenHeight>
          {isLoading && (
            <SkeletonText data-testid="shopper-preference-skeleton">
              <SkeletonTextLine />
              <SkeletonTextLine />
              <SkeletonTextLine />
            </SkeletonText>
          )}
          {!isLoading && (
            <Typography>
              {fulfillmentPreferencesMessages.whiteLabelShippingDetails}
            </Typography>
          )}
        </Card>
      </FlexBox>
    </FlexBox>
  );
};
